import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { RootState } from '../../../../../../store/reducers/rootReducer'
import { IPalletDetailsForDivide } from '../types/palletDetailsForDivide'
import { IPalletTitleItem } from '../../../../../modules/API/Pallets/models/palletTitleItem'
import { IPalletListWithDetails } from '../../../../../modules/API/Pallets/models/palletList'
import { IPalletTypeItem } from '../../../../../modules/API/Defaults/models/pallets'
import ClientSelector, {
    ClientItem,
} from '../../../../../generic/ui/ClientSelector/ClientSelector'
import IconTextField from '../../../../../generic/ui/Input/IconTextField'
import PalletStackIcon from '../../../../../generic/icons/IconLib/PalletStackIcon'
import HashIcon from '../../../../../generic/icons/IconLib/HashIcon'
import { SearchOutlineIcon } from '../../../../../generic/icons/inputIcons'
import { AccentButton } from '../../../../../generic/ui/Button/AccentButton'
import { Loader } from '../../../../../generic/ui/Loader'
import PalletTypeSelector from '../../../../../generic/ui/Selectors/PalletTypeSelector'
import PalletDivideItemCard from './PalletDivideItemCard'

type Props = {
    isLoading: boolean
    palletTypeList: IPalletTypeItem[]
    selectedPalletTitle: IPalletTitleItem
    palletDetails: IPalletDetailsForDivide[]
    handleSave: (
        customerID: number,
        palletNumber: number,
        palletTypeID: number,
        palletTypeQuantity: number,
    ) => void
    handleChangeQuantityForDivide: (recID: number, quantity: number) => void
    fetchPalletByID: (palletID: number) => Promise<IPalletListWithDetails | undefined>
    withoutPalletID: number
}

const DividePalletModal: React.FC<Props> = ({
    isLoading,
    palletDetails,
    handleSave,
    handleChangeQuantityForDivide,
    fetchPalletByID,
    palletTypeList,
    withoutPalletID,
}) => {
    const [t] = useTranslation('buttons')
    const customerList = useSelector(
        (state: RootState) => state.defaults.clientList.list,
    ) as ClientItem[] | null

    const [customerID, setCustomerID] = useState<number>(0)
    const [palletNumber, setPalletNumber] = useState<number>(0)
    const [isFoundPallet, setIsFoundPallet] = useState<boolean>(false)
    const [palletTypeID, setPalletTypeID] = useState<number>(withoutPalletID)
    const [palletTypeQuantity, setPalletTypeQuantity] = useState<number>(1)

    const handleChangePalletNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsFoundPallet(false)
        setPalletNumber(Number(e.target.value))
    }

    const handleGetPalletByID = async () => {
        if (!palletNumber) return
        const res = await fetchPalletByID(palletNumber)
        setIsFoundPallet(!!res)
    }

    const handleSavePallet = () => {
        handleSave(customerID, palletNumber, palletTypeID, palletTypeQuantity)
    }

    const isAllowToAddPallet = !isFoundPallet

    const isAllowToSave = useMemo(() => {
        const isValidPalletNumber =
            (isFoundPallet && palletNumber !== 0) || (!isFoundPallet && !palletNumber)
        return (
            isValidPalletNumber &&
            palletDetails.some((item) => item.QuantityForDivide > 0) &&
            palletTypeID !== 0
        )
    }, [palletDetails, palletTypeID, isFoundPallet, palletNumber])

    const customerObj = useMemo(
        () => (customerList ?? []).find((c) => +c.CustomerID === customerID) ?? null,
        [customerList, customerID],
    )
    const palletTypeObj = useMemo(
        () => palletTypeList.find((p) => p.Pallete_Type === palletTypeID) ?? null,
        [palletTypeList, palletTypeID],
    )

    return (
        <FormShell>
            <FormGrid>
                <Field>
                    <Label>לקוח למשטח</Label>
                    <ClientSelector
                        clients={customerList ?? []}
                        value={customerObj}
                        onSelect={(c) => setCustomerID(c ? +c.CustomerID : 0)}
                    />
                </Field>
                <Field>
                    <Label>מספר משטח</Label>
                    <IconTextField
                        type='number'
                        leadingIcon={<PalletStackIcon />}
                        value={palletNumber || ''}
                        onChange={handleChangePalletNumber}
                        onFocus={(e) => e.currentTarget.select()}
                        endAdornment={
                            <SearchBtn
                                type='button'
                                onClick={handleGetPalletByID}
                                disabled={!palletNumber}
                                aria-label='search pallet'
                            >
                                <SearchOutlineIcon />
                            </SearchBtn>
                        }
                    />
                </Field>
            </FormGrid>

            {isAllowToAddPallet && (
                <FormGrid>
                    <Field>
                        <Label>סוג משטח</Label>
                        <PalletTypeSelector
                            palletTypes={palletTypeList}
                            value={palletTypeObj}
                            onSelect={(p) => setPalletTypeID(p?.Pallete_Type ?? 0)}
                        />
                    </Field>
                    <Field>
                        <Label>כמות משטחים</Label>
                        <IconTextField
                            type='number'
                            leadingIcon={<HashIcon />}
                            value={palletTypeQuantity}
                            onChange={(e) =>
                                setPalletTypeQuantity(Number(e.target.value))
                            }
                            onFocus={(e) => e.currentTarget.select()}
                        />
                    </Field>
                </FormGrid>
            )}

            <List>
                {isLoading ? (
                    <CenterState>
                        <Loader size={48} />
                    </CenterState>
                ) : (
                    palletDetails.map((d) => (
                        <PalletDivideItemCard
                            key={d.RecID}
                            {...d}
                            handleChangeQuantityForDivide={handleChangeQuantityForDivide}
                        />
                    ))
                )}
            </List>

            <Footer>
                <AccentButton
                    type='button'
                    onClick={handleSavePallet}
                    disabled={!isAllowToSave}
                    fullWidth={false}
                >
                    {t('save')}
                </AccentButton>
            </Footer>
        </FormShell>
    )
}

export default DividePalletModal

const FormShell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    width: 100%;
`

const FormGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem 1.25rem;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        grid-template-columns: 1fr;
        gap: 0.625rem 0.5rem;
    }
`

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    min-width: 0;
`

const Label = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: 0.75rem;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.ink2};
    text-align: start;
`

const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`

const CenterState = styled.div`
    min-height: 6rem;
    display: flex;
    align-items: center;
    justify-content: center;
`

const SearchBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    padding: 0;
    border: 0;
    background: transparent;
    color: ${({ theme }) => theme.colors.ink3};
    cursor: pointer;
    border-radius: 0.5rem;
    transition:
        color ${({ theme }) => theme.motion.fast} ease,
        background ${({ theme }) => theme.motion.fast} ease;

    svg {
        width: 1rem;
        height: 1rem;
    }

    &:hover:not(:disabled) {
        color: ${({ theme }) => theme.colors.ink};
        background: ${({ theme }) => theme.colors.line2};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`

const Footer = styled.div`
    position: sticky;
    bottom: -1.25rem;
    z-index: 2;
    display: flex;
    justify-content: flex-start;
    padding: 0.75rem 0;
    margin-top: 0.5rem;
    border-top: 1px solid ${({ theme }) => theme.colors.line};
    background: ${({ theme }) => theme.colors.surface};

    button {
        min-width: 8rem;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        bottom: -1rem;
        button {
            width: 100%;
        }
    }
`
